import { AiModel } from "./interfaces/AiModel";
import { getAllAiProviders, getNonEmbeddingAiModels } from "./server";

export interface Provider {
  id: number;
  providerName: string;
}

export async function fetchProviders(): Promise<Provider[]> {
  const providersData = await getAllAiProviders();

  if (!providersData) {
    throw new Error("Failed to load providers from API");
  }

  return providersData.map((provider: any) => ({
    id: provider.id,
    providerName: provider.providerName,
  }));
}

export async function fetchNonEmbeddingModels(): Promise<AiModel[]> {
  const modelsData = await getNonEmbeddingAiModels();

  if (!modelsData) {
    throw new Error("Failed to load non-embedding models from API");
  }

  return modelsData.map((model: any) => ({
    id: model.id,
    providerId: model.providerId,
    modelName: model.modelName,
    inputTokenCost: model.inputTokenCost,
    outputTokenCost: model.outputTokenCost,
    enabled: model.enabled,
    apiModelName: model.apiModelName,
  }));
}

export function findModelByProviderId(allModels: AiModel[], providerId: number): AiModel[] {
  return allModels.filter(
    (model) => model.providerId === providerId && model.enabled
  );
}

export async function getProviderById(
  providerId: number
): Promise<Provider | undefined> {
  const providers = await fetchProviders();
  return providers.find((provider) => provider.id === providerId);
}

export async function getProviderByNameValue(
  nameValue: string
): Promise<Provider | undefined> {
  const providers = await fetchProviders();
  return providers.find((provider) => provider.providerName === nameValue);
}

export async function getProviderByName(
  name: string
): Promise<Provider | undefined> {
  const providers = await fetchProviders();
  return providers.find((provider) => provider.providerName === name);
}

export async function getModelId(model: string): Promise<number | undefined> {
  const modelsData = await getNonEmbeddingAiModels();

  if (!modelsData) {
    throw new Error("Failed to load models from API");
  }

  return modelsData.find((modelData: any) => 
    modelData.modelName === model || modelData.apiModelName === model
  )?.id;
}

export async function getApiModelName(modelName: string): Promise<string | undefined> {
  const modelsData = await getNonEmbeddingAiModels();

  if (!modelsData) {
    throw new Error("Failed to load models from API");
  }

  return modelsData.find((modelData: any) => modelData.modelName === modelName)?.apiModelName;
}

export function getApiModelNameFromCache(allModels: AiModel[], modelName: string): string | undefined {
  return allModels.find((model) => model.modelName === modelName)?.apiModelName;
}
